import { makeAutoObservable, runInAction } from "mobx";
import { EventBus, PDFFindController, FindState } from "pdfjs-dist/web/pdf_viewer.mjs";
import type { PDFDocumentProxy } from "pdfjs-dist";
import { highlightTextItem } from "./highlight-text-item";

const DEBOUNCE_MS = 200;

export { FindState };

export class PdfFindStore {
  /** Whether the find bar UI is visible. */
  isOpen = false;
  /** Current search query string. */
  query = "";
  /** Current and total match counts reported by PDFFindController. */
  matchCount = { current: 0, total: 0 };
  /** PDFFindController result state (FOUND, NOT_FOUND, WRAPPED, PENDING). */
  state: number = FindState.FOUND;
  /** 0-based page index of the currently selected match (-1 when none). */
  selectedPageIndex = -1;

  /**
   * Monotonically increasing counter that triggers customTextRenderer
   * re-creation. Only bumped when the search fully settles
   * (updatefindcontrolstate) or when navigating between matches, avoiding
   * per-page flicker during incremental text extraction.
   */
  private _renderVersion = 0;

  private _eventBus: InstanceType<typeof EventBus> | null = null;
  private _controller: InstanceType<typeof PDFFindController> | null = null;
  private _debounceTimer: ReturnType<typeof setTimeout> | null = null;
  /** Tracks cumulative character offset per page for customTextRenderer. */
  private _pageOffsets = new Map<number, number>();

  constructor() {
    makeAutoObservable(this);
  }

  open = () => {
    this.isOpen = true;
  };

  close = () => {
    this.isOpen = false;
    this.query = "";
    this._clearHighlights();
  };

  setQuery = (q: string) => {
    this.query = q;
    this._debouncedFind();
  };

  findNext = () => {
    if (!this.query) return;
    this._renderVersion++;
    this._dispatchFind(true);
  };

  findPrevious = () => {
    if (!this.query) return;
    this._renderVersion++;
    this._dispatchFind(true, true);
  };

  initController = (pdfDocument: PDFDocumentProxy) => {
    this._dispose();

    this._eventBus = new EventBus();

    // SimpleLinkService.setDocument() is a no-op, so pagesCount stays 0 and
    // text extraction never runs. Use a minimal stub that exposes the real
    // page count directly from the PDFDocumentProxy. PDFFindController only
    // reads pagesCount and page from the link service.
    const linkService = { pagesCount: pdfDocument.numPages, page: 1 } as never;

    this._controller = new PDFFindController({
      linkService,
      eventBus: this._eventBus,
    });

    this._controller.onIsPageVisible = () => true;

    this._eventBus.on("updatefindmatchescount", this._handleMatchesCount as unknown as () => void);
    this._eventBus.on("updatefindcontrolstate", this._handleControlState as unknown as () => void);

    this._controller.setDocument(pdfDocument);
  };

  reset = () => {
    this.isOpen = false;
    this.query = "";
    this.matchCount = { current: 0, total: 0 };
    this.state = FindState.FOUND;
    this.selectedPageIndex = -1;
    this._renderVersion = 0;
    this._clearHighlights();
  };

  dispose = () => {
    this._dispose();
  };

  /**
   * Returns a customTextRenderer function for react-pdf's <Page>.
   *
   * react-pdf calls customTextRenderer once per text item in reading order.
   * PDFFindController.pageMatches contains character offsets relative to the
   * *concatenated* text of the entire page. We track cumulative offsets per
   * page so we can map global match offsets to individual text items.
   *
   * Each render pass resets the cumulative offset for a page when itemIndex=0.
   */
  get customTextRenderer():
    | ((props: { pageIndex: number; pageNumber: number; itemIndex: number; str: string }) => string)
    | undefined {
    if (!this._controller || !this.query) return undefined;

    // Read _renderVersion to establish a MobX dependency. This counter
    // only increments when the search settles (updatefindcontrolstate)
    // or when navigating between matches, so the page doesn't re-render
    // on every incremental match found during text extraction.
    void this._renderVersion;

    const controller = this._controller;
    const pageOffsets = this._pageOffsets;
    const query = this.query;

    return ({
      pageIndex,
      itemIndex,
      str,
    }: {
      pageIndex: number;
      pageNumber: number;
      itemIndex: number;
      str: string;
    }) => {
      // Reset cumulative offset at the start of each page render
      if (itemIndex === 0) {
        pageOffsets.set(pageIndex, 0);
      }

      const itemStart = pageOffsets.get(pageIndex) ?? 0;
      const itemEnd = itemStart + str.length;

      // Advance cumulative offset for next item
      pageOffsets.set(pageIndex, itemEnd);

      const pageMatches = controller.pageMatches?.[pageIndex];
      const pageMatchesLength = controller.pageMatchesLength?.[pageIndex];
      const selected = controller.selected;

      if (!pageMatches || pageMatches.length === 0) return str;

      return highlightTextItem(
        str,
        itemStart,
        itemEnd,
        pageIndex,
        pageMatches,
        pageMatchesLength,
        selected,
        query
      );
    };
  }

  // ── private ──────────────────────────────────────────────────

  private _debouncedFind() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => {
      this._dispatchFind(false);
    }, DEBOUNCE_MS);
  }

  private _dispatchFind(again?: boolean, reverse?: boolean) {
    if (!this._eventBus) return;

    this._eventBus.dispatch("find", {
      source: this,
      type: again ? "again" : "",
      query: this.query,
      caseSensitive: false,
      entireWord: false,
      highlightAll: true,
      findPrevious: !!reverse,
    });
  }

  private _clearHighlights() {
    if (!this._eventBus) return;
    this._eventBus.dispatch("find", {
      source: this,
      type: "",
      query: "",
      caseSensitive: false,
      entireWord: false,
      highlightAll: true,
      findPrevious: false,
    });
  }

  private _handleMatchesCount = (evt: { matchesCount: { current: number; total: number } }) => {
    runInAction(() => {
      this.matchCount = {
        current: evt.matchesCount.current,
        total: evt.matchesCount.total,
      };
    });
  };

  private _handleControlState = (evt: {
    state: number;
    matchesCount: { current: number; total: number };
  }) => {
    runInAction(() => {
      this.state = evt.state;
      this.matchCount = {
        current: evt.matchesCount.current,
        total: evt.matchesCount.total,
      };
      this._renderVersion++;
      if (this._controller?.selected) {
        this.selectedPageIndex = this._controller.selected.pageIdx;
      }
    });
  };

  private _dispose() {
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = null;
    }
    if (this._eventBus) {
      this._eventBus.off(
        "updatefindmatchescount",
        this._handleMatchesCount as unknown as () => void
      );
      this._eventBus.off(
        "updatefindcontrolstate",
        this._handleControlState as unknown as () => void
      );
    }
    this._controller = null;
    this._eventBus = null;
  }
}
